import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, IconButton, Tooltip, Chip, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRenderer from '../TableRenderer';
import { useEntityNames, entityDisplayName } from '../../services/EntityNameService';
import FilterChipBar from './FilterChipBar';
import StructuredSearchField from './StructuredSearchField';
import AdvancedSearchPanel from './AdvancedSearchPanel';
import FlexService from '../../services/FlexService';
import {
  buildTableData, resolveAdvancedSearchFieldMeta, resolveSmartSearchFieldMeta, withCustomFieldKeys, liftDefaultChipsForSearch, defaultChipFilters,
} from './workspaceUtils';
import { useColumnVisibility, applyColumnVisibility } from './fieldVisibility';
import WorkspaceChart from './WorkspaceChart';
import OrderableListPanel from './OrderableListPanel';
import SchedulePanel from './SchedulePanel';
import useHiddenRowNotice, { HiddenRowNotice } from './useHiddenRowNotice';

/**
 * WorkspaceLayoutView - Renders content blocks in a grid layout.
 *
 * When a view has a `layout` property with `rows` and `columns`,
 * this component renders content blocks arranged in a CSS grid
 * instead of the default master/detail stack.
 *
 * Each content block gets its own independent data loading and rendering.
 */
const WorkspaceLayoutView = ({ view, colors, fieldMetaCache, onRowAction, onGlobalAction, onLinkClick, onOpenWorkspace, onNavigateToView, onError, onSuccess, onConfigureTable, pendingNavParamsRef, refreshToken, workspaceNavParams }) => {
  useEntityNames();
  const layout = view.layout;
  const contentBlocks = view.content || [];
  const gap = layout?.gap || 8;
  const viewToolbar = view.toolbar || null;
  const viewFilters = view.filters || null;

  // Independent data state per content block
  const columnVisibleByScript = useColumnVisibility(view);
  const [blockData, setBlockData] = useState({});
  const [blockLoading, setBlockLoading] = useState({});
  // Server paging (FH-204): how many records each grid's list has in all, which batch is being
  // added, and the params and sort its rows were loaded with
  const [blockTotal, setBlockTotal] = useState({});
  const [blockLoadingMore, setBlockLoadingMore] = useState({});
  const blockDataRef = React.useRef({});
  const lastParamsRef = React.useRef({});
  const sortRef = React.useRef({});
  // the latest full load of each block (fetchBlockData), so an earlier one answering late is ignored
  const loadSeqRef = React.useRef({});
  // opened by a link with its own filter: read once, at mount -- the workspace clears the pending filter after the
  // first effect run, and a second run (development) would otherwise put the default chips back
  const openedByLinkRef = React.useRef(!!pendingNavParamsRef?.current);
  blockDataRef.current = blockData;
  const [selectedRow, setSelectedRow] = useState(null);

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedSearchParams, setAdvancedSearchParams] = useState({});
  const [activeFilters, setActiveFilters] = useState([]);
  const [navParams, setNavParams] = useState(null);
  const [presetSearch, setPresetSearch] = useState(null);
  const hiddenRow = useHiddenRowNotice(viewFilters?.chips, activeFilters);
  const isSearching = Boolean(searchQuery.trim())
    || Object.values(advancedSearchParams || {}).some((v) => v !== '' && v != null);

  // Field metadata for the search box's suggestion dropdown and the Advanced Search panel —
  // shared resolution logic with the master/detail renderer (useWorkspaceData.js) so both
  // behave identically. Uses the first non-child block's entity as the metadata-cache fallback
  // key (only matters when advancedSearchFields isn't declared in the object format).
  // the grid the toolbar searches: the first top-level grid (not a KPI summary above it)
  const primaryBlock = useMemo(
    () => contentBlocks.find((c) => !c.parentContent && !c.$ref && c.loadScript && (c.type || 'grid') === 'grid')
      || contentBlocks.find((c) => !c.parentContent && !c.$ref),
    [contentBlocks]
  );
  const primaryEntity = primaryBlock?.entity;
  const advancedSearchFieldMeta = useMemo(
    () => resolveAdvancedSearchFieldMeta(viewToolbar, fieldMetaCache || {}, primaryEntity),
    [viewToolbar, fieldMetaCache, primaryEntity]
  );
  // Smart Search offers the declared fields, else the grid's columns, as on a plain grid view
  const smartSearchFieldMeta = useMemo(
    () => resolveSmartSearchFieldMeta(advancedSearchFieldMeta, primaryBlock, fieldMetaCache || {}),
    [advancedSearchFieldMeta, primaryBlock, fieldMetaCache]
  );

  // Fetch data for a content block
  // opts.append adds the next batch of a paged list instead of replacing the rows
  const fetchBlockData = useCallback(async (block, params, opts = {}) => {
    if (!block.loadScript) return;
    // only the latest full load of a block may fill it: an earlier one answering late (the view's first load,
    // before a link's filter applied) used to replace the filtered rows with everything
    const seq = (loadSeqRef.current[block.id] || 0) + (opts.append ? 0 : 1);
    if (!opts.append) loadSeqRef.current[block.id] = seq;
    const stale = () => !opts.append && loadSeqRef.current[block.id] !== seq;
    if (opts.append) {
      setBlockLoadingMore(prev => ({ ...prev, [block.id]: true }));
    } else {
      if (!block.parentContent) lastParamsRef.current[block.id] = params || {};
      // only show the spinner the first time; refreshes swap data in place so the grid keeps its position
      setBlockLoading(prev => (prev[block.id] === undefined ? { ...prev, [block.id]: true } : prev));
    }
    try {
      const context = {};
      if (params && Object.keys(params).length > 0) context._queryParams = params;
      const response = await FlexService.executeScript({ scriptName: block.loadScript, context });
      if (stale()) return;
      if (response.success) {
        const ctx = response.context || {};
        let data = ctx.result || ctx.data;
        if (data && !Array.isArray(data)) {
          if (Array.isArray(data.data)) data = data.data;
          else data = [data];
        }
        const rows = (data || []).map(withCustomFieldKeys);
        const total = typeof ctx.total === 'number' ? ctx.total : null;
        if (opts.append) {
          const key = block.primaryKey;
          setBlockData(prev => {
            const have = prev[block.id] || [];
            const seen = new Set(key ? have.map(r => String(r[key])) : []);
            return { ...prev, [block.id]: [...have, ...rows.filter(r => !key || !seen.has(String(r[key])))] };
          });
        } else {
          setBlockData(prev => ({ ...prev, [block.id]: rows }));
        }
        setBlockTotal(prev => ({ ...prev, [block.id]: total }));
      } else if (!opts.append) {
        setBlockData(prev => ({ ...prev, [block.id]: [] }));
      }
    } catch (err) {
      console.error(`Failed to load block ${block.id}:`, err);
      if (!opts.append && !stale()) setBlockData(prev => ({ ...prev, [block.id]: [] }));
    } finally {
      if (opts.append) setBlockLoadingMore(prev => ({ ...prev, [block.id]: false }));
      else setBlockLoading(prev => ({ ...prev, [block.id]: false }));
    }
  }, []);

  // The next batch, up to the row the user paged to (FH-204)
  const loadMoreBlock = useCallback((block, upTo) => {
    const have = (blockDataRef.current[block.id] || []).length;
    if (have >= upTo) return;
    const params = { ...(lastParamsRef.current[block.id] || {}), offset: have, limit: Math.max(100, upTo - have) };
    fetchBlockData(block, params, { append: true });
  }, [fetchBlockData]);

  // Sort the whole list on the server when only part of it is loaded
  const sortBlock = useCallback((block, column, direction) => {
    sortRef.current[block.id] = { sort: column, order: direction };
    const { offset, limit, sort, order, ...rest } = lastParamsRef.current[block.id] || {}; // eslint-disable-line no-unused-vars
    fetchBlockData(block, { ...rest, sort: column, order: direction });
  }, [fetchBlockData]);


  // Initialize default filters from chips; a link's filter (an order opened from its shipment) lifts the default chips,
  // as a search does, so the record shows whatever its status
  useEffect(() => {
    if (viewFilters?.chips) {
      const defaults = defaultChipFilters(viewFilters.chips);
      setActiveFilters(openedByLinkRef.current || pendingNavParamsRef?.current ? liftDefaultChipsForSearch(defaults, viewFilters.chips).filters : defaults);
    } else {
      setActiveFilters([]);
    }
    setSearchQuery('');
    // read, not cleared: the workspace's hook (useWorkspaceData) runs after this and keeps them for the view. This
    // view is mounted once per tab, so it starts empty and a second run (development) must not empty it again.
    if (pendingNavParamsRef?.current) {
      setAdvancedSearchParams(pendingNavParamsRef.current);
      setNavParams(pendingNavParamsRef.current);
    }
  }, [view.viewId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build merged params from filters + search
  const buildMergedParams = useCallback(() => {
    const merged = {};
    if (activeFilters.length > 0) {
      const byField = {};
      activeFilters.forEach(({ field, value }) => {
        if (!byField[field]) byField[field] = [];
        byField[field].push(String(value));
      });
      Object.entries(byField).forEach(([field, values]) => { merged[field] = values.join(','); });
    }
    if (searchQuery.trim()) merged.q = searchQuery.trim();
    if (Object.keys(advancedSearchParams).length > 0) Object.assign(merged, advancedSearchParams);
    return merged;
  }, [activeFilters, searchQuery, advancedSearchParams]);

  // Load all non-child content blocks
  // opts.keepLoaded (a refresh after an action): reload as many rows as are loaded, so the grid
  // doesn't drop back to the first batch while the user is on a later page
  const loadAllBlocks = useCallback((opts = {}) => {
    const mergedParams = buildMergedParams();
    contentBlocks.forEach(block => {
      if (!block.parentContent && block.loadScript && (block.type === 'grid' || block.type === 'summary')) {
        const params = { ...(block.loadParams || {}), ...mergedParams, ...(sortRef.current[block.id] || {}) };
        const loaded = (blockDataRef.current[block.id] || []).length;
        if (opts.keepLoaded && loaded > 100) params.limit = loaded;
        fetchBlockData(block, params);
      }
    });
  }, [contentBlocks, fetchBlockData, buildMergedParams]);

  // Load on mount and when filters/search change. A new parent filter/search
  // invalidates whatever was previously selected — clear the selection and any
  // child blocks' data so a stale child grid (e.g. Order Lines) doesn't keep
  // showing results for a row that's no longer in the new filtered set.
  useEffect(() => {
    hiddenRow.reset();
    setSelectedRow(null);
    setBlockData(prev => {
      const next = { ...prev };
      contentBlocks.forEach(block => {
        if (block.parentContent) delete next[block.id];
      });
      return next;
    });
    loadAllBlocks();
  }, [view.viewId, activeFilters, advancedSearchParams, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // A row acted on that the refresh no longer shows: say where it went (FH-201)
  useEffect(() => {
    contentBlocks.forEach((b) => { if (!b.parentContent) hiddenRow.check(b.id, blockData[b.id]); });
  }, [blockData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle row selection in a parent block — load child blocks
  const handleRowSelect = useCallback((block, rowData) => {
    setSelectedRow({ blockId: block.id, data: rowData });

    // Find child blocks that depend on this block. Orderable-list children
    // manage their own loading (keyed off the `context` prop) so they're
    // excluded here — routing them through fetchBlockData too would double
    // the load calls for no benefit, since blockData isn't read by that block type.
    contentBlocks.forEach(child => {
      if (child.parentContent === block.id && child.loadScript && child.type !== 'orderable-list') {
        const params = {};
        if (child.parentParams) {
          Object.entries(child.parentParams).forEach(([paramKey, rowField]) => {
            params[paramKey] = rowData[rowField];
          });
        }
        fetchBlockData(child, params);
      }
    });
  }, [contentBlocks, fetchBlockData]);

  // A link that opens this view on one record (an order from its shipment) shows that record's own grids too: the one
  // row it finds is selected, as if clicked, so its lines show instead of an empty "Nothing matches" below it
  useEffect(() => {
    if (!openedByLinkRef.current || selectedRow) return;
    const parent = contentBlocks.find((b) => !b.parentContent && contentBlocks.some((c) => c.parentContent === b.id));
    const rows = parent ? blockData[parent.id] : null;
    if (rows && rows.length === 1) handleRowSelect(parent, rows[0]);
  }, [blockData, selectedRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh after an edit/action elsewhere in the workspace: reload blocks and the selected row's child grids in place
  const firstRefresh = React.useRef(true);
  useEffect(() => {
    if (firstRefresh.current) { firstRefresh.current = false; return; }
    loadAllBlocks({ keepLoaded: true });
    if (selectedRow) {
      const parent = contentBlocks.find(b => b.id === selectedRow.blockId);
      if (parent) handleRowSelect(parent, selectedRow.data);
    }
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render a single content block
  // one grid and nothing else: let it fill the pane instead of leaving blank space below
  const soleBlock = layout.rows.length === 1 && (layout.rows[0].columns || []).length === 1;

  const renderBlock = (block) => {
    const data = blockData[block.id] || [];
    const loading = blockLoading[block.id];

    if (block.type === 'summary') {
      // KPI summary cards
      if (loading) return <CircularProgress size={24} sx={{ color: '#22c55e' }} />;
      const metrics = block.metrics || [];
      return (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {metrics.map(metric => {
            const value = data[0]?.[metric.key] ?? data[metric.key] ?? 0;
            return (
              <Box key={metric.key} sx={{
                flex: '1 1 150px', p: 2, bgcolor: colors.surface,
                border: `1px solid ${colors.border}`, textAlign: 'center',
              }}>
                <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                  {value}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                  {metric.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    if (block.type === 'chart') {
      return (
        <WorkspaceChart
          config={block}
          colors={colors}
          context={{ _selectedRow: selectedRow?.data }}
          onNavigateToView={onNavigateToView}
        />
      );
    }

    if (block.type === 'grid') {
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#22c55e' }} />
          </Box>
        );
      }

      // Build title with template substitution from selected parent row
      let title = block.title || entityDisplayName(block.entity) || block.label || block.entity;
      if (block.parentContent) {
        if (selectedRow) {
          title = title.replace(/\{\{(\w+)\}\}/g, (_, key) => selectedRow.data?.[key] ?? '');
        } else if (/\{\{\w+\}\}/.test(title)) {
          // No parent row selected yet -- a templated title has nothing to
          // substitute into, so fall back to a plain label instead of
          // showing the raw {{token}} to the user.
          title = block.label || entityDisplayName(block.entity) || block.entity;
        }
      }

      const viewActions = view.actions || {};
      const built = buildTableData(block, data, title, view.viewId, null, fieldMetaCache, viewActions);
      const table = { ...built, columns: applyColumnVisibility(built.columns, block.columnVisibility, columnVisibleByScript) };
      if (!block.parentContent && blockTotal[block.id] != null) {
        table.paging = {
          total: blockTotal[block.id],
          loading: !!blockLoadingMore[block.id],
          loadMore: (upTo) => loadMoreBlock(block, upTo),
          sort: (column, direction) => sortBlock(block, column, direction),
        };
      }

      return (
        <>
          <TableRenderer
            table={table}
            fillHeight={soleBlock}
            colors={colors}
            onRowAction={(action, row) => {
              // If this block has children, select the row to load them
              const hasChildren = contentBlocks.some(c => c.parentContent === block.id);
              const rowData = row.data || row;
              if (hasChildren) handleRowSelect(block, rowData);
              if (!block.parentContent) {
                hiddenRow.watch({
                  source: block.id, row: rowData, keyField: block.primaryKey,
                  loadScript: block.loadScript, loadParams: block.loadParams, data: blockData[block.id],
                });
              }
              if (onRowAction) onRowAction(action, row);
            }}
            onBulkAction={(action, ids) => {
              const selectedData = (table.rows || []).filter(r => ids.includes(r.rowId)).map(r => r.data);
              const actionType = (action.actionType || action.type || '').toUpperCase();
              if (actionType === 'BULK_CONFIRM' && onRowAction) {
                onRowAction(action, { selected_ids: ids, selected_rows: selectedData });
              } else if (onGlobalAction) {
                onGlobalAction({ ...action, _extraContext: { selected_ids: ids, selected_rows: selectedData } });
              }
            }}
            onGlobalAction={onGlobalAction}
            onLinkClick={(columnId, value, rowData) => {
              // Select row for child loading
              const hasChildren = contentBlocks.some(c => c.parentContent === block.id);
              if (hasChildren) handleRowSelect(block, rowData);
              if (onLinkClick) onLinkClick(columnId, value, rowData);
            }}
            // a link column with a linkAction opens another screen on that record (an order's shipments)
            onOpenWorkspace={onOpenWorkspace}
            onRowSelect={contentBlocks.some(c => c.parentContent === block.id)
              ? (rowData) => handleRowSelect(block, rowData) : undefined}
            onConfigureTable={onConfigureTable}
          />
          {data.length === 0 && block.emptyState && (
            // A search that matches nothing is not an empty queue: never say "all done" for it (FH-81)
            isSearching ? (
              <Box sx={{ textAlign: 'center', py: 4 }} data-testid="search-no-match">
                <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                  Nothing matches your search
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1.5 }}>
                  {searchQuery.trim() ? `No rows match "${searchQuery.trim()}".` : 'No rows match the search.'}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => { handleSearchClear(); setPresetSearch({ text: '' }); }}
                  sx={{ textTransform: 'none' }}>
                  Clear search
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 0.5 }}>
                  {block.emptyState.title || 'No data'}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                  {block.emptyState.message || ''}
                </Typography>
              </Box>
            )
          )}
        </>
      );
    }

    // FH-373: a day schedule (Dock Management > Appointments); a link that opened this view may name its day
    if (block.type === 'schedule') {
      return (
        <SchedulePanel
          config={block}
          colors={colors}
          context={workspaceNavParams || navParams}
          refreshToken={refreshToken}
          onError={onError}
          onRowAction={(action, item) => onRowAction && onRowAction(action, item)}
        />
      );
    }

    if (block.type === 'orderable-list') {
      if (block.parentContent) {
        if (!selectedRow || selectedRow.blockId !== block.parentContent) {
          return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                Select a row above to manage this list.
              </Typography>
            </Box>
          );
        }
      }

      let panelContext;
      if (block.parentContent && block.parentParams) {
        panelContext = {};
        Object.entries(block.parentParams).forEach(([paramKey, rowField]) => {
          panelContext[paramKey] = selectedRow.data?.[rowField];
        });
      } else if (!block.parentContent && (workspaceNavParams || navParams)) {
        // a view opened from another one's row (NAVIGATE contextParams): its list is that row's. The workspace's
        // copy is used first: this view can mount after the workspace has already taken the values.
        panelContext = workspaceNavParams || navParams;
      }

      let panelTitle = block.title;
      if (selectedRow && block.parentContent && panelTitle) {
        panelTitle = panelTitle.replace(/\{\{(\w+)\}\}/g, (_, key) => selectedRow.data?.[key] ?? '');
      }

      return (
        <OrderableListPanel
          config={panelTitle !== block.title ? { ...block, title: panelTitle } : block}
          colors={colors}
          fieldMetaCache={fieldMetaCache}
          context={panelContext}
          onError={onError}
          onSaved={onSuccess}
          refreshToken={refreshToken}
          onRowAction={(action, item) => onRowAction && onRowAction(action, item)}
          onGlobalAction={onGlobalAction}
          // a header action's prefill reads the parent row, or the values this view was opened with
          parentRow={block.parentContent ? selectedRow?.data : panelContext}
        />
      );
    }

    return null;
  };

  // Search handlers
  // A search looks across every status: the view's default chips (its starting view) are lifted
  // while searching and come back when the search is cleared (FH-203)
  const defaultsLiftedRef = React.useRef(false);
  const handleSearch = useCallback((params) => {
    const { q, ...fieldParams } = params;
    setSearchQuery(q || '');
    setAdvancedSearchParams(fieldParams);
    if ((q || '').trim() || Object.keys(fieldParams).length > 0) {
      setActiveFilters((prev) => {
        const { filters, lifted } = liftDefaultChipsForSearch(prev, viewFilters?.chips);
        if (lifted) defaultsLiftedRef.current = true;
        return filters;
      });
    } else if (defaultsLiftedRef.current) {
      // an emptied search is a cleared one
      defaultsLiftedRef.current = false;
      setActiveFilters((prev) => (prev.length === 0 ? defaultChipFilters(viewFilters?.chips) : prev));
    }
  }, [viewFilters]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setAdvancedSearchParams({});
    if (defaultsLiftedRef.current) {
      defaultsLiftedRef.current = false;
      setActiveFilters((prev) => (prev.length === 0 ? defaultChipFilters(viewFilters?.chips) : prev));
    }
  }, [viewFilters]);

  // "Show it": drop the filter that hides the record and search for it, so it's found past the newest page too
  const showHiddenRow = useCallback((key) => {
    defaultsLiftedRef.current = true;
    setActiveFilters([]);
    setAdvancedSearchParams({});
    setSearchQuery(key);
    setPresetSearch({ text: key });
  }, []);

  const handleAdvancedSearch = useCallback((params) => {
    setAdvancedSearchParams(params);
  }, []);

  const handleAdvancedSearchClear = useCallback(() => {
    setAdvancedSearchParams({});
  }, []);

  // Render rows and columns from layout
  if (!layout?.rows) return null;

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Toolbar */}
      {viewToolbar && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 0.75,
          borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
          position: 'relative',
        }}>
          {viewToolbar.showSearch && (
            <StructuredSearchField
              fieldMetadata={smartSearchFieldMeta}
              onSearch={handleSearch}
              onClear={handleSearchClear}
              colors={colors}
              placeholder={viewToolbar.searchPlaceholder || 'Search...'}
              externalParams={navParams}
              presetText={presetSearch}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {viewToolbar.showAdvancedSearch && (
            <AdvancedSearchPanel
              fieldMetadata={advancedSearchFieldMeta}
              onSearch={handleAdvancedSearch}
              onClear={handleAdvancedSearchClear}
              colors={colors}
            />
          )}
          {viewToolbar.showRefresh && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={loadAllBlocks}
                sx={{ color: colors.textSecondary, '&:hover': { color: '#22c55e' } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Header action buttons (for dashboard views with charts). A grid/summary
          content block falls back to view.actions.header for its own toolbar
          (buildJsonActions in workspaceUtils.js) when it declares no header
          actions of its own, so rendering this bar too would show every button
          twice for any view that mixes a top-level actions.header with a grid --
          e.g. Replenishment Work's "Trigger Replenishment"/"Start Replenishment
          Pick" duplicated above AND inside the grid's own toolbar. Only render
          here when nothing else already owns them. */}
      {view.actions?.header?.length > 0 && !contentBlocks.some((b) => b.type === 'grid' || b.type === 'summary') && (
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 0.75, flexWrap: 'wrap' }}>
          {view.actions.header.map((action, i) => (
            <Button
              key={action.actionScript || i}
              size="small"
              variant={action.variant || 'outlined'}
              onClick={() => onGlobalAction && onGlobalAction({
                actionId: action.actionScript || `header-${i}`,
                label: action.label,
                type: action.type || 'SCRIPT',
                actionType: action.type || 'SCRIPT',
                scriptName: action.actionScript,
                confirmMessage: action.confirmMessage || action.confirm || null,
                formEntity: action.formEntity,
              })}
              sx={{
                color: action.variant === 'contained' ? '#fff' : colors.textPrimary,
                borderColor: colors.border,
                bgcolor: action.variant === 'contained' ? '#22c55e' : 'transparent',
                '&:hover': { bgcolor: action.variant === 'contained' ? '#16a34a' : colors.hoverBg },
                textTransform: 'none',
                fontSize: '0.8rem',
              }}
            >
              {action.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Filter chips */}
      {viewFilters && (
        <FilterChipBar
          filters={viewFilters}
          fieldMetadata={[]}
          activeFilters={activeFilters}
          onFilterChange={setActiveFilters}
          colors={colors}
        />
      )}
      <HiddenRowNotice notice={hiddenRow.notice} onShow={showHiddenRow} onClose={hiddenRow.dismiss} />

      {/* Layout grid */}
      <Box sx={{ flex: 1, overflow: soleBlock ? 'hidden' : 'auto', p: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {layout.rows.map((row, rowIdx) => (
        <Box
          key={rowIdx}
          sx={{
            display: 'flex',
            gap: `${gap}px`,
            mb: `${gap}px`,
            ...(soleBlock ? { flex: 1, mb: 0 } : {}),
            minHeight: row.height === 'equal' ? 0 : undefined,
            height: row.height && row.height !== 'auto' && row.height !== 'equal' ? row.height : undefined,
          }}
        >
          {(row.columns || []).map((col, colIdx) => {
            const block = contentBlocks.find(b => b.id === col.contentId);
            if (!block) return null;

            const widthPercent = (col.width / 12) * 100;

            return (
              <Box
                key={col.contentId || colIdx}
                sx={{
                  width: `${widthPercent}%`,
                  minWidth: 0,
                  minHeight: col.minHeight || undefined,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.background,
                }}
              >
                {renderBlock(block)}
              </Box>
            );
          })}
        </Box>
      ))}
      </Box>
    </Box>
  );
};

export default WorkspaceLayoutView;
